import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../supabase';
import {
  classifyAssignment,
  formatDate,
  STATUS_LABELS,
  STATUS_EMOJI,
  STATUS_ORDER,
} from '../utils';
import QuizFlow from './QuizFlow';
import { downloadCertificate } from './certificate';

export default function Dashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [completionsByModule, setCompletionsByModule] = useState({});
  const [activeAssignment, setActiveAssignment] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [assignmentsResult, completionsResult] = await Promise.all([
      supabase
        .from('inservice_assignments')
        .select(
          `id, status, due_date, assigned_at, completed_at, module_id,
           module:inservice_modules ( id, title, category, description, content,
             passing_score, estimated_minutes, expiration_days )`
        )
        .eq('user_id', user.id)
        .order('due_date', { ascending: true, nullsFirst: false }),
      supabase
        .from('inservice_completions')
        .select('id, module_id, passed, score, certificate_id, completed_at')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false }),
    ]);

    if (assignmentsResult.error) {
      setError(assignmentsResult.error.message || 'Failed to load assignments');
      setAssignments([]);
      setLoading(false);
      return;
    }

    setAssignments(assignmentsResult.data || []);

    const map = {};
    for (const c of completionsResult.data || []) {
      const list = map[c.module_id] || (map[c.module_id] = []);
      list.push(c);
    }
    setCompletionsByModule(map);
    setLoading(false);
  }, [user.id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const grouped = useMemo(() => {
    const groups = { overdue: [], due_soon: [], assigned: [], completed: [] };
    for (const a of assignments) {
      const k = classifyAssignment(a);
      groups[k].push(a);
    }
    return groups;
  }, [assignments]);

  if (loading) {
    return (
      <main className="app-main">
        <div className="loading-state">Loading your training…</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="app-main">
        <div className="empty-state">
          <h3>Unable to load training</h3>
          <p>{error}</p>
        </div>
      </main>
    );
  }

  if (assignments.length === 0) {
    return (
      <main className="app-main">
        <div className="empty-state">
          <h3>No training assigned</h3>
          <p>Contact your administrator.</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="app-main">
        <h1 className="page-title">My Training</h1>
        <p className="page-subtitle">
          Complete required in-service modules before their due date.
        </p>

        {STATUS_ORDER.map((key) => {
          const list = grouped[key];
          if (!list || list.length === 0) return null;
          return (
            <section key={key} className="status-section">
              <h2>
                <span aria-hidden="true">{STATUS_EMOJI[key]}</span>
                {STATUS_LABELS[key]}
                <span className="count">({list.length})</span>
              </h2>
              <div className="card-grid">
                {list.map((a) => (
                  <AssignmentCard
                    key={a.id}
                    assignment={a}
                    statusKey={key}
                    completions={completionsByModule[a.module_id] || []}
                    user={user}
                    onAction={() => setActiveAssignment(a)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </main>

      {activeAssignment && (
        <QuizFlow
          assignment={activeAssignment}
          user={user}
          onClose={() => setActiveAssignment(null)}
          onCompleted={async () => {
            await loadAll();
          }}
        />
      )}
    </>
  );
}

function AssignmentCard({ assignment, statusKey, completions, user, onAction }) {
  const m = assignment.module || {};
  const isCompleted = statusKey === 'completed';
  const statusClass = statusKey.replace('_', '-');

  const latestPass = completions.find((c) => c.passed);
  const hasFailedAttempt = !isCompleted && completions.some((c) => !c.passed);

  let buttonLabel = 'Start';
  if (isCompleted) buttonLabel = 'View Certificate';
  else if (assignment.status === 'in_progress') buttonLabel = 'Continue';
  else if (hasFailedAttempt) buttonLabel = 'Retake';

  function handleClick() {
    if (isCompleted && latestPass) {
      downloadCertificate({
        userName: user.name || user.email,
        moduleTitle: m.title,
        certificateId: latestPass.certificate_id,
        completedAt: latestPass.completed_at,
        score: latestPass.score,
      });
      return;
    }
    onAction();
  }

  return (
    <article className={`card ${statusClass}`}>
      <div className="card-title">{m.title || 'Untitled module'}</div>
      {m.category && <span className="category-badge">{m.category}</span>}
      <div className="card-meta">
        {m.estimated_minutes && <span>{m.estimated_minutes} min</span>}
        {assignment.due_date && (
          <span className="due">Due {formatDate(assignment.due_date)}</span>
        )}
      </div>
      <span className={`badge ${statusClass}`}>{STATUS_LABELS[statusKey]}</span>
      <div className="card-actions">
        <button
          type="button"
          className={isCompleted ? 'btn btn-gold' : 'btn btn-primary'}
          onClick={handleClick}
        >
          {buttonLabel}
        </button>
      </div>
    </article>
  );
}
